import { createFileRoute, Link } from "@tanstack/react-router";
import { SETTING_OUTPATIENT_ES } from "@/lib/medical-schema";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const URL = `${SITE}/es/balon-gastrico`;

/** Spanish FAQs; also emitted as FAQPage structured data below. */
const FAQS = [
  {
    q: "¿El balón gástrico es una cirugía?",
    a: "No. El balón gástrico se coloca y se retira por endoscopia, a través de la boca, con sedación ligera. No hay incisiones ni cambios permanentes en su anatomía.",
  },
  {
    q: "¿Cuánto tiempo permanece el balón en el estómago?",
    a: "Alrededor de seis meses. Después debe retirarse por endoscopia. El dispositivo va acompañado de un programa de nutrición y cambio de hábitos de doce meses.",
  },
  {
    q: "¿Cuánto peso se pierde con el balón gástrico?",
    a: "En promedio, los pacientes pierden cerca del 11 al 13 por ciento de su peso total durante los seis meses que el balón está colocado, siempre que sigan el programa de alimentación y actividad física. La pérdida de peso es menor que con la manga gástrica o el bypass gástrico.",
  },
  {
    q: "¿Cuáles son los efectos secundarios?",
    a: "Náuseas, vómitos y molestia estomacal son frecuentes en los primeros días y suelen calmarse pronto. Con menos frecuencia pueden presentarse reflujo, úlceras gástricas y, en casos raros, desinflado o desplazamiento del balón. Una minoría de pacientes necesita que se le retire antes de tiempo.",
  },
  {
    q: "¿Voy a recuperar el peso cuando me retiren el balón?",
    a: "Si no se mantienen los cambios de alimentación y actividad, la recuperación de peso es lo habitual. El balón es una herramienta temporal, no una cura; por eso el acompañamiento de doce meses importa tanto como el dispositivo.",
  },
  {
    q: "¿El seguro cubre el balón gástrico?",
    a: "Casi nunca. La mayoría de los planes considera el balón intragástrico un servicio no cubierto, así que en la práctica es un procedimiento de pago propio. Con gusto verificamos sus beneficios por si su plan es una excepción.",
  },
  {
    q: "¿Cuánto cuesta el balón gástrico?",
    a: "Se cotiza como un precio único de pago propio que incluye la endoscopia de colocación, la sedación, el retiro a los seis meses y el programa de nutrición. Le damos el precio exacto y vigente en la consulta, y ofrecemos financiamiento con pagos mensuales a través de Prosper Healthcare Lending.",
  },
];

export const Route = createFileRoute("/es/balon-gastrico")({
  head: () => {
    const base = esHead("balloon");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("balloon"),
              {
                "@type": "MedicalProcedure",
                "@id": `${URL}#procedimiento`,
                name: "Balón gástrico (Orbera®)",
                alternateName: ["Balón intragástrico", "Gastric balloon"],
                description:
                  "Procedimiento endoscópico sin cirugía en el que se coloca un balón de silicona en el estómago durante unos seis meses para ayudar a comer porciones más pequeñas y bajar de peso.",
                bodyLocation: "estómago",
                procedureType: "https://schema.org/NoninvasiveProcedure",
                howPerformed:
                  "Mediante una endoscopia breve, con sedación ligera y a través de la boca, se introduce un balón de silicona en el estómago y se llena con solución salina estéril. Se retira por endoscopia a los seis meses." + " " + SETTING_OUTPATIENT_ES,
                preparation:
                  "Evaluación médica previa, orientación nutricional y una dieta de líquidos claros antes de la colocación.",
                followup:
                  "Doce meses de seguimiento médico y nutricional, incluidos los seis meses con el balón colocado.",
                url: URL,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
              {
                "@type": "FAQPage",
                "@id": `${URL}#preguntas`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${URL}#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsBalloon,
});

function EsBalloon() {
  return (
    <EsPageShell
      page="balloon"
      title="Balón gástrico (Orbera®)"
      intro="El balón gástrico es una opción para bajar de peso sin cirugía: un balón blando de silicona se coloca en el estómago por endoscopia, permanece unos seis meses y le ayuda a sentirse satisfecho con porciones más pequeñas."
    >
      <EsSection id="como-funciona" heading="En qué consiste el procedimiento">
        <p>
          El balón intragástrico se coloca en una endoscopia ambulatoria de unos veinte a treinta
          minutos, con sedación ligera y a través de la boca. No hay incisiones, no se corta nada
          y no se modifica su anatomía. Una vez adentro, el balón se llena con solución salina
          estéril hasta quedar aproximadamente del tamaño de una toronja.
        </p>
        <p>
          Al ocupar espacio en el estómago, usted se llena antes y come porciones más pequeñas. El
          balón permanece seis meses y luego se retira, también por endoscopia. El dispositivo es
          solo la mitad del tratamiento: se acompaña de un programa de doce meses de nutrición,
          cambios de alimentación supervisados y modificación de hábitos.
        </p>
      </EsSection>

      <EsSection id="resultados" heading="Cuánto peso se pierde">
        <p>
          En promedio, los pacientes pierden entre el 11 y el 13 por ciento de su peso total
          durante los seis meses con el balón colocado. Es menos de lo que se logra con la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          o el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          , pero sin cirugía y de forma temporal.
        </p>
        <p>
          Los resultados varían de una persona a otra. Lo que más influye a largo plazo no es el
          balón en sí, sino los hábitos que se construyen mientras está colocado.
        </p>
      </EsSection>

      <EsSection id="candidatos" heading="¿Soy candidato?">
        <p>
          El balón gástrico suele considerarse en adultos con un índice de masa corporal (IMC)
          aproximado de 30 a 40 que no han logrado bajar de peso con dieta y ejercicio, o que
          prefieren empezar sin una operación. También puede servir para reducir peso antes de
          otro procedimiento.
        </p>
        <p>
          No es apropiado si ha tenido cirugía previa del estómago, hernia hiatal grande, úlceras
          activas o ciertas condiciones digestivas. En la consulta revisamos su historia clínica y
          le decimos con franqueza si esta es la mejor opción para usted.
        </p>
      </EsSection>

      <EsSection id="riesgos" heading="Riesgos y efectos secundarios">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Náuseas, vómitos y molestia estomacal en los primeros días; generalmente se controlan
            con medicamentos y ceden pronto.
          </li>
          <li>Reflujo o acidez, que a veces requiere tratamiento durante los seis meses.</li>
          <li>Con menor frecuencia: úlceras gástricas.</li>
          <li>
            En casos raros: desinflado o desplazamiento del balón, que obliga a retirarlo antes de
            tiempo.
          </li>
          <li>
            Recuperación de peso después del retiro si no se mantienen los cambios de
            alimentación y actividad física.
          </li>
        </ul>
      </EsSection>

      <EsSection id="precios" heading="Precios y formas de pago">
        <p>
          Casi ningún seguro cubre el balón gástrico: la mayoría de los planes lo clasifica como
          un servicio de control de peso no cubierto, igual que Medicare y Medicaid de Virginia.
          En la práctica, es un procedimiento de <strong>pago propio</strong>. Aun así, con gusto
          verificamos sus beneficios por si su plan es una excepción; puede ver cómo funciona la
          cobertura en nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            seguro médico
          </Link>
          .
        </p>
        <p>
          Nuestro precio de pago propio es un <strong>paquete único y transparente</strong>. No
          hay cargos sorpresa después del procedimiento. El paquete incluye:
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>La consulta de evaluación y la preparación previa.</li>
          <li>La endoscopia de colocación y la sedación.</li>
          <li>La endoscopia de retiro a los seis meses.</li>
          <li>
            El programa de doce meses de nutrición y cambio de hábitos, con seguimiento médico.
          </li>
        </ul>
        <p>
          <strong>Financiamiento.</strong> Ofrecemos pagos mensuales a través de Prosper
          Healthcare Lending, con solicitud en línea y respuesta rápida. Muchos pacientes también
          usan su cuenta HSA o FSA para cubrir parte del costo.
        </p>
        <p className="text-sm text-muted-foreground">
          Los precios cambian con el tiempo, así que no publicamos una cifra que pueda quedar
          desactualizada. Llámenos al (703) 709-9771 o{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            escríbanos
          </Link>{" "}
          y le damos el precio vigente del paquete, en español y sin compromiso.
        </p>
        <p>
          También puede ver el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-balon-orbera"
            className="font-medium text-brand-logo hover:underline"
          >
            costo del balón gástrico Orbera
          </Link>{" "}
          con pago propio en nuestra página de precios de pago propio, junto con el resto de los
          procedimientos.
        </p>
      </EsSection>


      <EsSection id="norte-de-virginia" heading="Balón gástrico Orbera en el norte de Virginia">
        <p>
          El balón se coloca por vía endoscópica como procedimiento ambulatorio en INOVA Fair Oaks Hospital en Fairfax, sin incisiones y sin pasar la noche. Los seis meses de seguimiento y acompañamiento que lo acompañan se realizan en nuestro consultorio de Herndon o por telesalud.
        </p>
        <p>
          Para otra opción sin cirugía, vea la <Link to="/es/gastroplastia-endoscopica-en-manga" className="text-brand-deep underline hover:no-underline">gastroplastia endoscópica en manga (ESG) en el Norte de Virginia</Link>. También puede conocer la <Link to="/es/perdida-de-peso-con-medicamentos" className="text-brand-deep underline hover:no-underline">pérdida de peso con medicamentos en el Norte de Virginia</Link> o la <Link to="/es/manga-gastrica" className="text-brand-deep underline hover:no-underline">manga gástrica en el Norte de Virginia</Link>.
        </p>
        <p>
          ¿Preguntas sobre el balón Orbera? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_balloon_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>

              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>
    </EsPageShell>
  );
}
